import { useState, useEffect, useCallback, useMemo } from 'react'
import { useAuth } from '../../hooks/useAuth'
import { useDialog } from '../../hooks/useDialog'
import { usePagination } from '../../hooks/usePagination'
import catalogService from '../../services/catalogService'
import { Unit, UnitPayload } from '../../types/catalog'
import { Button } from '../../components/ui/Button'
import { Dialog } from '../../components/ui/Dialog'
import { Pagination } from '../../components/ui/Pagination'
import { UnitForm } from './UnitForm'
import {
  PageHeader,
  PageMeta,
  PageEyebrow,
  PageTitle,
  TableWrapper,
  StyledTable,
  THead,
  TH,
  EmptyState,
  TableSkeleton,
} from '../../components/ui/Table'
import { useConfirm } from '../../hooks/useConfirm'
import { ConfirmDialog } from '../../components/ui/ConfirmDialog'
import { UnitRow } from './UnitRow'

export function UnitsPage() {
  const { token } = useAuth()
  const { page, perPage, setPage } = usePagination()
  const dialog = useDialog<Unit>()

  const [items, setItems] = useState<Unit[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [payload, setPayload] = useState<UnitPayload | null>(null)
  const svc = useMemo(() => catalogService(token), [token])
  const { units } = svc

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await units.list(page, perPage)
      setItems(res.items)
      setTotal(res.total)
    } finally {
      setLoading(false)
    }
  }, [page, perPage, units])

  useEffect(() => {
    load()
  }, [load])

  const handleSave = useCallback(async () => {
    if (!payload) return
    setSaving(true)
    try {
      if (dialog.data) {
        await svc.units.update(dialog.data.id, payload)
      } else {
        await svc.units.create(payload)
      }
      dialog.close()
      load()
    } finally {
      setSaving(false)
    }
  }, [dialog, payload, svc, load])

  const confirmDialog = useConfirm()

  const handleDelete = useCallback(
    (item: Unit) => {
      confirmDialog.confirm(
        'Delete unit',
        `Are you sure you want to delete "${item.translations.find(t => t.locale === 'en')?.name ?? item.code}"? This action cannot be undone.`,
        async () => {
          await svc.units.delete(item.id)
          load()
        }
      )
    },
    [confirmDialog, svc, load]
  )

  return (
    <>
      <PageHeader>
        <PageMeta>
          <PageEyebrow>Catalog</PageEyebrow>
          <PageTitle>Units</PageTitle>
        </PageMeta>
        <Button onClick={() => dialog.open()}>+ Add unit</Button>
      </PageHeader>

      {loading ? (
        <TableSkeleton cols={6} />
      ) : (
        <>
          <TableWrapper>
            <StyledTable>
              <THead>
                <tr>
                  <TH>Code</TH>
                  <TH>Type</TH>
                  <TH>English</TH>
                  <TH>Abbr (en)</TH>
                  <TH>Spanish</TH>
                  <TH>Abbr (es)</TH>
                  <TH>Grams eq.</TH>
                  <TH />
                </tr>
              </THead>
              <tbody>
                {items.length === 0 && (
                  <tr>
                    <td colSpan={8}>
                      <EmptyState message="No units yet." />
                    </td>
                  </tr>
                )}
                {items.map(item => (
                  <UnitRow key={item.id} item={item} onEdit={dialog.open} onDelete={handleDelete} />
                ))}
              </tbody>
            </StyledTable>
          </TableWrapper>
          <Pagination page={page} total={total} perPage={perPage} onChange={setPage} />
        </>
      )}

      <Dialog
        open={dialog.isOpen}
        title={dialog.data ? 'Edit unit' : 'Add unit'}
        onClose={dialog.close}
        onConfirm={handleSave}
        confirmLabel={dialog.data ? 'Save changes' : 'Add unit'}
        confirmLoading={saving}
        confirmDisabled={!payload?.code?.trim()}
        width="600px"
      >
        <UnitForm initial={dialog.data} onChange={setPayload} />
      </Dialog>
      <ConfirmDialog
        open={confirmDialog.state.open}
        title={confirmDialog.state.title}
        message={confirmDialog.state.message}
        confirmLabel="Delete"
        onConfirm={confirmDialog.state.onConfirm}
        onCancel={confirmDialog.cancel}
        loading={confirmDialog.loading}
        variant="danger"
      />
    </>
  )
}
